// src/theme.js
import { createTheme } from '@mui/material/styles';

// Standardized M3 shapes
const m3Shapes = {
  borderRadius: 8, // default fallback
  small: 8,
  medium: 12,
  large: 16,
  extraLarge: 28,
  full: 9999,
};

// Common typography settings aligned with M3 specs
const m3Typography = {
  fontFamily: '"DM Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  
  // Display styles (large titles/prominent text)
  h1: {
    fontFamily: '"Baton Nouveau Web-Condensed", "DM Sans", "Inter", sans-serif',
    fontWeight: 700,
    fontSize: '2.5rem',
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
    textTransform: 'uppercase',
  },
  h2: {
    fontFamily: '"Baton Nouveau Web-Condensed", "DM Sans", "Inter", sans-serif',
    fontWeight: 700,
    fontSize: '2rem',
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    textTransform: 'uppercase',
  },
  h3: {
    fontFamily: '"Baton Nouveau Web-Condensed", "DM Sans", "Inter", sans-serif',
    fontWeight: 600,
    fontSize: '1.75rem',
    letterSpacing: '-0.01em',
    lineHeight: 1.25,
    textTransform: 'uppercase',
  },
  
  // Headline styles
  h4: {
    fontFamily: '"Baton Nouveau Web-Condensed", "DM Sans", "Inter", sans-serif',
    fontWeight: 600,
    fontSize: '1.5rem',
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
    textTransform: 'uppercase',
  },
  h5: {
    fontFamily: '"Baton Nouveau Web-Condensed", "DM Sans", "Inter", sans-serif',
    fontWeight: 600,
    fontSize: '1.25rem',
    lineHeight: 1.35,
    textTransform: 'uppercase',
  },
  
  // Title styles
  h6: {
    fontFamily: '"Baton Nouveau Web-Condensed", "DM Sans", "Inter", sans-serif',
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.4,
    textTransform: 'uppercase',
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  
  // Body styles
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
    letterSpacing: '0.015em',
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
    letterSpacing: '0.02em',
  },
  
  // Label styles (buttons, captions, tags)
  button: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 500,
    letterSpacing: '0.03em',
  },
  overline: {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
};

// Component overrides for Material Design 3
const getM3ComponentOverrides = (mode, palette) => {
  const solidBg = palette.background.paper;
  const solidCard = palette.background.card;
  const thinOutline = mode === 'dark' ? 'rgba(181, 235, 185, 0.15)' : 'rgba(0, 65, 73, 0.1)';
  const glassStyle = {
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
  };

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
          color: palette.text.primary,
          scrollbarColor: mode === 'dark' ? '#183538 #031618' : '#cbd5e1 #f2f5f8',
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            backgroundColor: mode === 'dark' ? '#031618' : '#f2f5f8',
            width: 8,
            height: 8,
          },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            backgroundColor: mode === 'dark' ? '#183538' : '#cbd5e1',
            minHeight: 24,
          },
          "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus": {
            backgroundColor: mode === 'dark' ? '#305053' : '#94a3b8',
          },
          "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active": {
            backgroundColor: mode === 'dark' ? '#305053' : '#94a3b8',
          },
          "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
            backgroundColor: mode === 'dark' ? '#305053' : '#94a3b8',
          },
          // Universal keyboard focus styling
          "*:focus-visible": {
            outline: `3px solid ${palette.primary.main} !important`,
            outlineOffset: '2px !important',
            borderRadius: '4px !important',
          },
          // Exempt inputs, textareas, and select elements to avoid annoying focus rings on click/focus
          "input:focus, textarea:focus, select:focus, input:focus-visible, textarea:focus-visible, select:focus-visible, .MuiInputBase-input:focus, .MuiInputBase-input:focus-visible": {
            outline: 'none !important',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: solidBg,
          ...glassStyle,
          backgroundImage: 'none',
          boxShadow: 'none',
          borderBottom: `1px solid ${thinOutline}`,
          color: palette.text.primary,
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: m3Shapes.full, // Pill-shape corner radius
          padding: '10px 24px',
          boxShadow: 'none',
          fontWeight: 600,
          transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
        },
        contained: {
          backgroundColor: palette.primary.main,
          color: palette.primary.contrastText,
          '&:hover': {
            backgroundColor: palette.primary.dark,
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: palette.outline,
          color: palette.primary.main,
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(181, 235, 185, 0.08)' : 'rgba(0, 65, 73, 0.04)',
            borderColor: palette.primary.main,
          },
        },
        text: {
          color: palette.primary.main,
          padding: '10px 16px',
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(181, 235, 185, 0.08)' : 'rgba(0, 65, 73, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: m3Shapes.large, // 16px corner radius
          backgroundColor: solidCard,
          ...glassStyle,
          border: `1px solid ${thinOutline}`,
          boxShadow: mode === 'dark'
            ? '0px 4px 20px rgba(0, 0, 0, 0.4)'
            : '0px 4px 20px rgba(0, 65, 73, 0.04)',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          '&:hover': {
            backgroundColor: mode === 'dark' ? '#123035' : '#ffffff',
            borderColor: palette.primary.main,
            boxShadow: mode === 'dark'
              ? '0px 12px 30px rgba(181, 235, 185, 0.1)'
              : '0px 12px 30px rgba(0, 65, 73, 0.08)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '&:last-child': {
            paddingBottom: '24px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: m3Shapes.small, // 8px corners
            backgroundColor: palette.surfaceContainerLow,
            transition: 'border-color 0.2s, background-color 0.2s',
            '& fieldset': {
              borderColor: palette.outline,
            },
            '&:hover fieldset': {
              borderColor: palette.text.primary,
            },
            '&.Mui-focused fieldset': {
              borderColor: palette.primary.main,
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: palette.text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: m3Shapes.small,
          backgroundColor: palette.surfaceContainerLow,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: m3Shapes.small,
          backgroundColor: palette.surfaceContainerLow,
          border: `1px solid ${palette.outlineVariant}`,
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '12px 0',
            backgroundColor: palette.surfaceContainer,
          },
          overflow: 'hidden',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          borderBottom: `1px solid ${palette.outlineVariant}`,
          '&.Mui-expanded': {
            minHeight: 48,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '24px',
          backgroundColor: palette.background.default,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: m3Shapes.extraLarge, // 28px corners for M3 dialogs
          backgroundColor: solidBg,
          ...glassStyle,
          backgroundImage: 'none',
          boxShadow: mode === 'dark'
            ? '0px 24px 48px rgba(0, 0, 0, 0.5)'
            : '0px 24px 48px rgba(0, 65, 73, 0.12)',
          border: `1px solid ${thinOutline}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '24px 24px 16px',
          fontWeight: 600,
          fontSize: '1.25rem',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: palette.outlineVariant,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          minWidth: 'auto',
          padding: '12px 16px',
          color: palette.text.secondary,
          transition: 'color 0.2s',
          borderRadius: '8px 8px 0 0',
          '&.Mui-selected': {
            color: palette.primary.main,
          },
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${palette.outlineVariant}`,
        },
        indicator: {
          backgroundColor: palette.primary.main,
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.8125rem',
          height: '32px',
          borderRadius: m3Shapes.small, // 8px rounded corners for chips
          backgroundColor: palette.surfaceContainer,
          color: palette.text.primary,
          border: `1px solid ${palette.outlineVariant}`,
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: palette.surfaceContainerHigh,
          },
          '&.MuiChip-clickable:active': {
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: palette.outline,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${palette.outlineVariant}`,
          padding: '16px 20px',
          color: palette.text.primary,
        },
        head: {
          fontWeight: 700,
          color: palette.text.primary,
          backgroundColor: palette.surfaceContainerLow,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: solidBg,
          ...glassStyle,
          boxShadow: mode === 'dark'
            ? '0px 8px 32px rgba(0, 0, 0, 0.5)'
            : '0px 8px 32px rgba(0, 65, 73, 0.06)',
          borderRadius: m3Shapes.medium, // 12px corners for menus
          border: `1px solid ${thinOutline}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: m3Shapes.small,
          margin: '4px 8px',
          padding: '8px 16px',
          transition: 'all 0.15s',
          '&:hover': {
            backgroundColor: palette.surfaceContainerHigh,
          },
          '&.Mui-selected': {
            backgroundColor: palette.primaryContainer,
            color: palette.onPrimaryContainer,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: palette.primaryContainer,
              opacity: 0.9,
            },
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: palette.surfaceContainerLow,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: palette.surfaceContainer,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: solidBg,
          ...glassStyle,
          borderRight: `1px solid ${thinOutline}`,
          borderLeft: 'none',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 52,
          height: 32,
          padding: 0,
          display: 'flex',
        },
        switchBase: {
          padding: 4,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: palette.primary.main,
              opacity: 1,
              border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.5,
            },
          },
          '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: palette.primary.main,
            border: '6px solid #fff',
          },
        },
        thumb: {
          boxSizing: 'border-box',
          width: 24,
          height: 24,
        },
        track: {
          borderRadius: 16,
          backgroundColor: palette.outline,
          opacity: 1,
          transition: 'background-color 500ms',
        },
      },
    },
  };
};

// Brand colors derived from readablenglish.com
// Seed primary: #004149 (deep teal)
// Accent secondary: #b5ebb9 (mint green)
// Accent tertiary: #ffaf9c (peach/salmon)

const lightPalette = {
  mode: 'light',
  primary: {
    light: '#006d7b',
    main: '#004149', // Core Brand Teal
    dark: '#002226',
    contrastText: '#ffffff',
  },
  secondary: {
    light: '#ffdad2',
    main: '#ffaf9c', // Brand Peach/Salmon
    dark: '#c07f6e',
    contrastText: '#004149',
  },
  error: {
    light: '#ffdad6',
    main: '#ba1a1a',
    dark: '#410002',
    contrastText: '#ffffff',
  },
  warning: {
    light: '#ffe082',
    main: '#f57c00',
    dark: '#e65100',
  },
  info: {
    light: '#c2e7ff',
    main: '#0061a4',
    dark: '#001d35',
  },
  success: {
    light: '#d2ebd6',
    main: '#b5ebb9',
    dark: '#84b888',
  },
  background: {
    default: '#f2f5f8', // Brand off-white/gray-blue
    paper: '#ffffff',
    card: '#ffffff',
  },
  text: {
    primary: '#051417', // Very dark brand teal-black instead of neutral gray for brand authenticity!
    secondary: '#40585c', // Muted brand teal-gray
    disabled: 'rgba(5, 20, 23, 0.38)',
  },
  divider: '#dbe1e6', // Soft brand gray-blue divider
  grey: {
    50: '#f2f5f8',
    100: '#e8ecef',
    200: '#dee2e6',
    300: '#d3d7dc',
    400: '#b8c0c8',
    500: '#8e9aa5',
    600: '#647481',
    700: '#43505c',
    800: '#232d36',
    900: '#0c1319',
  },

  // M3 Custom Semantic Tokens
  primaryContainer: '#b5ebb9', // Mint green brand color container
  onPrimaryContainer: '#004149',
  secondaryContainer: '#ffdad2', // Peach brand color container
  onSecondaryContainer: '#3d0700',
  tertiary: '#ffaf9c', // Peach brand color
  tertiaryContainer: '#ffaf9c', // Peach brand accent color container
  onTertiaryContainer: '#3d0700',
  errorContainer: '#ffdad6',
  onErrorContainer: '#410002',
  
  surface: '#f2f5f8',
  onSurface: '#051417',
  surfaceVariant: '#e8ecef',
  onSurfaceVariant: '#40585c',
  outline: '#6f888c',
  outlineVariant: '#dbe1e6',
  
  surfaceContainerLowest: '#ffffff',
  surfaceContainerLow: '#f2f5f8',
  surfaceContainer: '#e8ecef',
  surfaceContainerHigh: '#dee2e6',
  surfaceContainerHighest: '#d3d7dc',
};

const darkPalette = {
  mode: 'dark',
  primary: {
    light: '#d1f7d5',
    main: '#b5ebb9', // Mint green brand color as primary main
    dark: '#005232',
    contrastText: '#031618',
  },
  secondary: {
    light: '#ffdad2',
    main: '#ffaf9c', // Peach brand color as secondary main
    dark: '#7a2f1a',
    contrastText: '#031618',
  },
  error: {
    light: '#ffb4ab',
    main: '#ffb4ab',
    dark: '#690005',
    contrastText: '#690005',
  },
  warning: {
    light: '#ffe082',
    main: '#ffb74d',
    dark: '#b35c00',
  },
  info: {
    light: '#7fc9ff',
    main: '#53b4ff',
    dark: '#004b75',
  },
  success: {
    light: '#b5ebb9',
    main: '#b5ebb9',
    dark: '#005232',
  },
  background: {
    default: '#031618', // Deep dark brand teal background
    paper: '#082327', // Solid dark brand teal surface
    card: '#082327',
  },
  text: {
    primary: '#ffffff', // Crisp white text
    secondary: '#b0c5c7', // Soft teal-gray text
    disabled: 'rgba(255, 255, 255, 0.38)',
  },
  divider: 'rgba(181, 235, 185, 0.15)',
  grey: {
    50: '#ffffff',
    100: '#f0f7f8',
    200: '#d0e2e5',
    300: '#b0c5c7',
    400: '#90a8ab',
    500: '#708b8e',
    600: '#506e71',
    700: '#305053',
    800: '#183538',
    900: '#082327',
  },

  // M3 Custom Semantic Tokens
  primaryContainer: '#082327',
  onPrimaryContainer: '#b5ebb9',
  secondaryContainer: '#12382c',
  onSecondaryContainer: '#b5ebb9',
  tertiary: '#ffaf9c', // Peach brand color
  tertiaryContainer: '#082327',
  onTertiaryContainer: '#ffaf9c',
  errorContainer: '#93000a',
  onErrorContainer: '#ffdad6',

  surface: '#031618',
  onSurface: '#ffffff',
  surfaceVariant: '#082327',
  onSurfaceVariant: '#b0c5c7',
  outline: '#6f888b',
  outlineVariant: '#3f5558',

  surfaceContainerLowest: '#01080a',
  surfaceContainerLow: '#021012',
  surfaceContainer: '#082327',
  surfaceContainerHigh: '#123035',
  surfaceContainerHighest: '#1c3e44',
};

export const lightTheme = createTheme({
  palette: lightPalette,
  shape: m3Shapes,
  typography: m3Typography,
  components: getM3ComponentOverrides('light', lightPalette),
});

export const darkTheme = createTheme({
  palette: darkPalette,
  shape: m3Shapes,
  typography: m3Typography,
  components: getM3ComponentOverrides('dark', darkPalette),
});

export default darkTheme;